import { useLocation } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BillingInfoButton from 'modules/billing/components/BillingInfoButton'
import ContactButton from 'modules/billing/components/ContactButton'
import { formatPrice } from 'modules/billing/lib/prices'
import { ChargebeeSubscription, getSimpleSubscriptionItems } from 'modules/billing/lib/prices'

import { FormatPrice } from './FormatPrice'

type Props = {
  isYearly: boolean
  totalPrice: number
  perSeatPrice: number
  currency: string
  seats: number
  minimumSeats: number
  bundleSubscriptionItems: ChargebeeSubscription[]
  isPersonalisedHub?: boolean
  disableButton?: boolean
  children?: JSX.Element
}

type F25Props = {
  bundleSubscriptionItems: ChargebeeSubscription[]
  children?: JSX.Element
  showContactSalesButton: boolean
}

const Trial = ({
  isYearly,
  totalPrice,
  perSeatPrice,
  currency,
  seats,
  minimumSeats,
  bundleSubscriptionItems,
  isPersonalisedHub,
  children,
  disableButton = false,
}: Props) => {
  const { pathname } = useLocation()
  const { navigation } = useNavigation()
  const isCustomBundlePage = pathname.includes(navigation.upgradePlan.customBundles.path)

  return (
    <>
      <Box paddingY="s24" paddingX={isPersonalisedHub ? 's0' : 's24'} gap="s12">
        <Box flexDirection="row" alignItems="flexEnd" gap="s4">
          <FormatPrice price={formatPrice(totalPrice, currency)} isYearly={isYearly} />
        </Box>
        <Box gap="s16">
          <Box gap="s24">
            <Box gap="s12">
              <BillingInfoButton
                planConfig={bundleSubscriptionItems}
                subscribedFrom={
                  isPersonalisedHub ? 'personalised_hub' : isCustomBundlePage ? 'custom_hub' : 'hub'
                }
                disabled={disableButton}
              />
              <Box gap="s4">
                <Box flexDirection="row" gap="s4" flexWrap="wrap">
                  <Text.Normal size="100">
                    {i18n.t('pricing_plans.employees_inludede', {
                      employees: Math.max(seats, minimumSeats),
                    })}
                    ,
                  </Text.Normal>
                  <Text.Normal size="100">
                    {i18n.t('pricing_plans.per_new_employee', {
                      amount: formatPrice(perSeatPrice, currency),
                    })}
                  </Text.Normal>
                </Box>
              </Box>
            </Box>
            {children}
          </Box>
        </Box>
      </Box>
    </>
  )
}

const F25Trial = ({ bundleSubscriptionItems, showContactSalesButton, children }: F25Props) => {
  const { pathname } = useLocation()
  const { company } = useCurrentSession()

  const emitUpgradeRequest = (requestType: 'Upsell' | 'Purchase') => {
    track(trackingEvents.billing.requestToUpgradeStart, {
      request_type: requestType,
      page_path: pathname,
      page_url: window.location.href,
      company_plan: company.subscription.inFreeTrial ? ['free'] : company.subscription.plan,
      product_plan: bundleSubscriptionItems.map((e) => getSimpleSubscriptionItems(e).itemId),
    })
  }

  return (
    <Box paddingY="s24" paddingX="s24" gap="s16">
      {showContactSalesButton ? (
        <ContactButton
          type="upgrade"
          ctaLabel={i18n.t('pricing_plans.contact_sales')}
          emitEventHandler={() => emitUpgradeRequest('Purchase')}
        />
      ) : (
        <BillingInfoButton planConfig={bundleSubscriptionItems} subscribedFrom="f25_plan" />
      )}
      {children}
    </Box>
  )
}

export default { Trial, F25Trial }
